import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Content from "@/components/specific/Content";
import { useDocumentTitle } from "@/utils/hooks/useDocumentTitle";
import numeral from "numeral";
import global from "@/utils/helpers/global";
import {
  getTagihanPelanggan,
  postTagihanPelanggan,
  postPaymentFinnet,
} from "@/utils/apis/public";
import "./infopelanggan.css";
import InfoBackground from "./InfoBackground";
import UsageChart from "./UsageChart";

function TagihanV2() {
  useDocumentTitle("Info Tagihan - Perumda Air Minum Cilegon Mandiri");

  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const history = searchParams.get("history");
  const lastPage = searchParams.get("last_page");

  const [searchValue, setSearchValue] = useState(history || "");
  const [isLoading, setIsLoading] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [riwayatUsage, setRiwayatUsage] = useState([]);
  const previousHistoryRef = useRef(null);

  // Group tagihan by year
  const groupTagihanByYear = (tagihan) => {
    const grouped = {};
    tagihan.forEach((item) => {
      const year = item.periode_tahun;
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(item);
    });
    return grouped;
  };

  // Calculate summary
  const calculateSummary = (tagihan) => {
    const list = Array.isArray(tagihan) ? tagihan : [];

    const isUnpaid = (item) => {
      if (item?.status_bayar === "S1") return true;
      const statusText = String(item?.status ?? "").toLowerCase();
      return statusText.includes("belum");
    };

    const getTotalInvoice = (item) => {
      const value = item?.total_invoice;
      if (Number.isFinite(value)) return value;
      const tagihanAir = parseFloat(item?.tagihan_air || 0);
      const biayaAdmin = parseFloat(item?.biaya_admin || 0);
      const danaMeter = parseFloat(item?.dana_meter || 0);
      const denda = parseFloat(item?.denda || 0);
      return tagihanAir + biayaAdmin + danaMeter + denda;
    };

    const unpaidItems = list.filter(isUnpaid);
    const unpaidAmount = unpaidItems.reduce(
      (sum, item) => sum + getTotalInvoice(item),
      0,
    );

    return {
      unpaidAmount,
      unpaidCount: unpaidItems.length,
      totalCount: list.length,
    };
  };

  const loadData = async (kode, preservePage = false) => {
    if (!kode) {
      setError("Mohon masukkan nomor sambung");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await postTagihanPelanggan(kode);

      // New Response structure: { success: true, data: { status: 200, message: "...", callback: [...] } }
      if (
        response?.success &&
        response?.data?.callback &&
        response?.data?.callback.length > 0
      ) {
        const callbackData = response.data.callback;

        // Extract pelanggan info from the first item
        const firstItem = callbackData[0];
        const pelangganData = {
          kode_pelanggan: firstItem.kode_pelanggan,
          nama_pelanggan: firstItem.nama_pelanggan,
          // Some fields might be missing in new response, handling gracefully
          alamat: firstItem.alamat || "-",
          golongan: {
            nama_golongan: firstItem.nama_golongan,
          },
        };

        // Map callback data to tagihan format
        const tagihanData = callbackData.map((item) => ({
          id_bill_invoice: item.id, // Using id as invoice id
          periode_bulan: item.periode_bulan,
          periode_tahun: item.periode_tahun,
          m3_awal: item.m3_awal,
          m3_akhir: item.m3_akhir,
          m3_pemakaian: item.m3_pemakaian,
          total_invoice:
            parseFloat(item.tagihan_air || 0) +
            parseFloat(item.biaya_admin || 0) +
            parseFloat(item.dana_meter || 0) +
            parseFloat(item.denda || 0),
          status: item.status_bayar === "S1" ? "Belum Lunas" : "Lunas", // Assuming S1 is Belum Lunas
          // Add other fields if needed
          ...item,
        }));

        const transformedData = {
          pelanggan: pelangganData,
          tagihan: tagihanData,
        };

        setData(transformedData);
        // Update previousHistoryRef untuk track
        previousHistoryRef.current = kode;
        // Update URL dengan history untuk maintain state
        // Jika preservePage true (dari auto-load), keep last_page, jika tidak reset ke 1
        const pageParam =
          preservePage && lastPage ? `&last_page=${lastPage}` : "";
        navigate(`/info-tagihan?history=${kode}${pageParam}`, {
          replace: true,
        });
      } else {
        setError(response?.data?.message || "Data pelanggan tidak ditemukan");
        setData(null);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Terjadi kesalahan saat mengambil data. Pastikan nomor sambung sudah benar.";
      setError(errorMessage);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    const kode = searchValue.trim();
    await loadData(kode, false);
  };

  const handlePayment = async () => {
    if (!data?.pelanggan?.kode_pelanggan) return;

    setIsPaymentLoading(true);
    try {
      const response = await postPaymentFinnet(data.pelanggan.kode_pelanggan);
      // Response from interceptor structure: { success: true, statusCode: 200, data: { status: 200, message: "...", callback: "..." } }
      const apiData = response?.data;

      if (apiData?.status === 200 && apiData?.callback) {
        // Clean URL in case of formatting artifacts (backticks, quotes, whitespace)
        const paymentUrl = apiData.callback.replace(/['"`]/g, "").trim();
        window.open(paymentUrl, "_blank");
      } else {
        // Fallback or error handling
        console.error("Payment response invalid:", response);
        alert(
          apiData?.message || "Gagal memproses pembayaran. Silakan coba lagi.",
        );
      }
    } catch (err) {
      console.error("Payment Error:", err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Terjadi kesalahan saat memproses pembayaran";
      alert(errorMessage);
    } finally {
      setIsPaymentLoading(false);
    }
  };

  // Auto-load data jika ada history di URL
  useEffect(() => {
    if (history && history !== "null") {
      setSearchValue(history);

      // Load jika history berbeda dengan sebelumnya
      // Atau jika data tidak ada (misalnya setelah kembali dari detail)
      if (history !== previousHistoryRef.current) {
        previousHistoryRef.current = history;
        loadData(history, true);
      } else if (!data) {
        // Jika history sama tapi data tidak ada, reload
        loadData(history, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]); // Run when history changes

  // Load full billing history (for the usage chart) whenever the customer code changes
  useEffect(() => {
    const kode = data?.pelanggan?.kode_pelanggan;
    if (!kode) {
      setRiwayatUsage([]);
      return;
    }

    let ignore = false;

    const fetchRiwayat = async () => {
      try {
        const response = await getTagihanPelanggan(kode);

        if (
          response?.success &&
          response?.data?.callback &&
          response?.data?.callback.length > 0
        ) {
          if (!ignore) {
            setRiwayatUsage(response.data.callback);
          }
        } else if (!ignore) {
          setRiwayatUsage([]);
        }
      } catch (err) {
        console.error("Error fetching riwayat tagihan for chart:", err);
        if (!ignore) {
          setRiwayatUsage([]);
        }
      }
    };

    fetchRiwayat();

    return () => {
      ignore = true;
    };
  }, [data?.pelanggan?.kode_pelanggan]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const groupedTagihan = data?.tagihan ? groupTagihanByYear(data.tagihan) : {};
  const sortedTagihan = data?.tagihan
    ? [...data.tagihan].sort((a, b) => {
        const byYear = (b.periode_tahun || 0) - (a.periode_tahun || 0);
        if (byYear !== 0) return byYear;
        return (b.periode_bulan || 0) - (a.periode_bulan || 0);
      })
    : [];
  const payableTagihan = sortedTagihan.filter((item) => {
    const value = item?.canPayOnline;
    return (
      value === true ||
      value === 1 ||
      value === "1" ||
      String(value).toLowerCase() === "true"
    );
  });
  const summary = data?.tagihan ? calculateSummary(payableTagihan) : null;
  const isPayDisabled = isPaymentLoading || payableTagihan.length === 0;

  const usageItems = (riwayatUsage || []).filter((item) =>
    Number.isFinite(parseFloat(item?.m3_pemakaian)),
  );

  // Deterministic decorative bubbles for the customer card (visual only)
  const custBubbles = Array.from({ length: 7 }, (_, i) => {
    const size = 6 + ((i * 37) % 18);
    const left = (i * 61) % 100;
    const duration = 7 + ((i * 53) % 9);
    const delay = (i * 70) % 9;
    return (
      <span
        key={i}
        className="bubble"
        style={{
          width: size,
          height: size,
          left: `${left}%`,
          animationDuration: `${duration}s`,
          animationDelay: `${delay}s`,
        }}
      />
    );
  });

  return (
    <>
      <InfoBackground />
      <Content showContentHeader={false}>
        <div className="ip">
          {/* Hero */}
          <div className="page-hero-ip">
            <div className="wrap">
              <div className="crumbs">
                <a href="https://www.pdamcilegon.co.id/">Beranda</a>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 6l6 6-6 6" />
                </svg>
                <span>Info Tagihan</span>
              </div>
              <span className="eyebrow">
                <span className="ic">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12h6m-6 4h6M7 3h7l5 5v11a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z" />
                  </svg>
                </span>
                Info Pelanggan
              </span>
              <h1>
                Cek Tagihan <span className="hl">Air Anda.</span>
              </h1>
              <p className="sub">
                Masukkan nomor sambung untuk melihat tagihan, rincian
                pemakaian, dan status pembayaran terkini.
              </p>
            </div>
          </div>

          <div className="section">
            <div className="wrap">
              {/* Search */}
              <div className="searchbar-ip">
                <div className="f">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="7" />
                    <path d="M21 21l-4-4" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Masukkan Nomor Sambung"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                  />
                </div>
                <button
                  className="btn-ip btn-primary"
                  onClick={handleSearch}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Mencari...
                    </>
                  ) : (
                    <>
                      Cari Tagihan
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M13 6l6 6-6 6" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
              {error && <div className="alert-ip">{error}</div>}

              {/* Loading State */}
              {isLoading && (
                <div className="state-card-ip mt-lg">
                  <span className="loading loading-spinner loading-lg text-[#095393]"></span>
                  <p className="mt">Memuat data tagihan...</p>
                </div>
              )}

              {/* Empty State */}
              {!isLoading && !data && !error && (
                <div className="state-card-ip mt-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-16 w-16 mx-auto text-gray-400 mb-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <p>Masukkan nomor sambung untuk melihat informasi tagihan</p>
                </div>
              )}

              {/* Data Display */}
              {!isLoading && data && (
                <>
                  {/* Customer Card */}
                  <div className="cust mt-lg">
                    {custBubbles}
                    <div className="who">
                      <span className="ava">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                          <circle cx="12" cy="8" r="4" />
                          <path d="M4 21a8 8 0 0116 0" />
                        </svg>
                      </span>
                      <div>
                        <div className="no">
                          NO. SAMBUNG · {data.pelanggan.kode_pelanggan}
                        </div>
                        <h3>{data.pelanggan.nama_pelanggan}</h3>
                        <span className="gol">
                          {data.pelanggan.golongan.nama_golongan}
                        </span>
                      </div>
                    </div>
                    <div className="act">
                      <Link
                        to={`/history-tagihan?history=${data.pelanggan.kode_pelanggan}`}
                        className="btn-ip btn-outline btn-sm"
                        style={{
                          background: "transparent",
                          borderColor: "rgba(255,255,255,.5)",
                          color: "#fff",
                        }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Riwayat Tagihan
                      </Link>
                      <button
                        className="btn-ip btn-teal btn-sm"
                        onClick={handlePayment}
                        disabled={isPayDisabled}
                      >
                        {isPaymentLoading ? (
                          <span className="loading loading-spinner loading-xs"></span>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="2" y="5" width="20" height="14" rx="2" />
                            <path d="M2 10h20" />
                          </svg>
                        )}
                        {isPaymentLoading ? "Memproses..." : "Bayar Tagihan"}
                      </button>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="stat-grid mt">
                    <div className="stat">
                      <div className="si i-rose">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                        </svg>
                      </div>
                      <div className="lbl">Total Belum Lunas</div>
                      <div className="val">
                        Rp. {numeral(summary.unpaidAmount).format("0,0")}
                      </div>
                    </div>
                    <div className="stat">
                      <div className="si i-amber">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <path d="M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
                          <path d="M12 9v4M12 17h.01" />
                        </svg>
                      </div>
                      <div className="lbl">Tagihan Belum Lunas</div>
                      <div className="val">
                        {summary.unpaidCount} <small>periode</small>
                      </div>
                    </div>
                    <div className="stat">
                      <div className="si i-blue">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <rect x="3" y="4" width="18" height="18" rx="2" />
                          <path d="M16 2v4M8 2v4M3 10h18" />
                        </svg>
                      </div>
                      <div className="lbl">Total Periode</div>
                      <div className="val">
                        {summary.totalCount} <small>bulan</small>
                      </div>
                    </div>
                  </div>

                  {/* Usage Chart */}
                  {usageItems.length > 0 && (
                    <div className="card card-pad mt-lg">
                      <div className="card-head">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                          <path d="M3 3v18h18" />
                          <path d="M7 14l3-4 3 3 4-6" />
                        </svg>
                        Grafik Pemakaian Air (m³)
                      </div>
                      <UsageChart items={usageItems} />
                    </div>
                  )}

                  <h3 className="section-title-ip">Rincian Tagihan</h3>

                  {payableTagihan.length === 0 ? (
                    <div className="state-card-ip">
                      Tidak Ada Tunggakan Tagihan Rekening Air
                    </div>
                  ) : (
                    payableTagihan.map((item) => (
                      <div className="inv" key={item.id_bill_invoice || item.id}>
                        <div className="inv-top">
                          <div className="per">
                            <span className="m">
                              {global.convert_month(item.periode_bulan)}
                            </span>
                            <span className="y">{item.periode_tahun}</span>
                          </div>
                          {item.status === "Lunas" ? (
                            <span className="pill pill-green">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6">
                                <path d="M20 6L9 17l-5-5" />
                              </svg>
                              LUNAS
                            </span>
                          ) : (
                            <span className="pill pill-red">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                                <path d="M18 6L6 18M6 6l12 12" />
                              </svg>
                              BELUM LUNAS
                            </span>
                          )}
                        </div>
                        <div className="inv-body">
                          <div className="meter">
                            <div className="mc">
                              <div className="k">Stan Awal</div>
                              <div className="v">
                                {numeral(item.m3_awal).format("0,0")}
                              </div>
                            </div>
                            <div className="mc">
                              <div className="k">Stan Akhir</div>
                              <div className="v">
                                {numeral(item.m3_akhir).format("0,0")}
                              </div>
                            </div>
                            <div className="mc hl">
                              <div className="k">Pemakaian</div>
                              <div className="v">
                                {numeral(item.m3_pemakaian).format("0,0")}{" "}
                                <small>m³</small>
                              </div>
                            </div>
                          </div>
                          <div className="break">
                            <div className="r">
                              <span>Tagihan Air</span>
                              <span>
                                Rp{" "}
                                {numeral(
                                  parseFloat(item.tagihan_air || 0),
                                ).format("0,0")}
                              </span>
                            </div>
                            <div className="r">
                              <span>Admin</span>
                              <span>
                                Rp{" "}
                                {numeral(
                                  parseFloat(item.biaya_admin || 0),
                                ).format("0,0")}
                              </span>
                            </div>
                            <div className="r">
                              <span>Dana Meter</span>
                              <span>
                                Rp{" "}
                                {numeral(
                                  parseFloat(item.dana_meter || 0),
                                ).format("0,0")}
                              </span>
                            </div>
                            <div className="r">
                              <span>Denda</span>
                              <span>
                                Rp{" "}
                                {numeral(parseFloat(item.denda || 0)).format(
                                  "0,0",
                                )}
                              </span>
                            </div>
                            <div className="r total">
                              <span>Total</span>
                              <b>
                                Rp{" "}
                                {numeral(
                                  parseFloat(item.tagihan_air || 0) +
                                    parseFloat(item.biaya_admin || 0) +
                                    parseFloat(item.dana_meter || 0) +
                                    parseFloat(item.denda || 0),
                                ).format("0,0")}
                              </b>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </Content>
    </>
  );
}

export default TagihanV2;
